import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'wouter';
import { ApiError, api } from '../lib/api';
import { shortDate } from '../lib/format';
import { fieldErrors } from '../lib/form-errors';
import { haptic } from '../lib/telegram';
import type { Client, ClientListItem, ListResponse } from '../types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  LoadingScreen,
  Modal,
  PageHeader,
  StatusBadge,
} from '../components/ui';

const CLIENT_FIELD_LABELS: Record<string, string> = {
  displayName: 'Имя',
  username: 'Ник в Telegram',
  telegramId: 'Telegram ID',
  notes: 'Заметки',
  tags: 'Теги',
};

const PAGE_SIZE = 20;

export function ClientsPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const params = new URLSearchParams({
    limit: String(PAGE_SIZE),
    offset: String(page * PAGE_SIZE),
  });
  if (search.trim()) params.set('query', search.trim());
  const query = useQuery({
    queryKey: ['clients', search.trim(), page],
    queryFn: () => api<ListResponse<ClientListItem>>(`/clients?${params.toString()}`),
  });
  const total = query.data?.total ?? 0;

  return (
    <>
      <PageHeader
        eyebrow="База"
        title="Клиенты"
        action={
          <Button variant="ghost" onClick={() => setCreating(true)}>
            Добавить
          </Button>
        }
      />
      <Field label="Поиск" hint="Имя, ник или Telegram ID">
        <Input
          value={search}
          placeholder="Анна или @anna"
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
        />
      </Field>

      {query.isPending && <LoadingScreen label="Открываем вашу базу" />}
      {query.error && <ErrorNotice error={query.error} />}
      {query.data?.items.length === 0 && (
        <EmptyState
          title={search ? 'Никого не нашли' : 'Клиентов пока нет'}
          text={
            search
              ? 'Попробуйте другое имя или ник — или добавьте человека вручную.'
              : 'Карточка заведётся сама, как только придёт первая заявка. А того, с кем договорились в личке, добавьте кнопкой «Добавить».'
          }
        />
      )}

      <div className="list">
        {query.data?.items.map((client) => (
          <Link key={client.id} href={`/clients/${client.id}`} className="card client-card">
            <div className="card-row">
              <h2>{client.displayName}</h2>
              {client.isBlocked && <StatusBadge tone="danger">Заблокирован</StatusBadge>}
            </div>
            <p className="muted">
              {client.username ? `@${client.username}` : 'Без ника'}
              {client.lastOrderAt ? ` · последний заказ ${shortDate(client.lastOrderAt)}` : ''}
            </p>
            {client.tags.length > 0 && (
              <div className="tag-list" aria-label="Теги клиента">
                {client.tags.map((tag) => (
                  <span key={tag} className="tag-pill">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <div className="card-row card-row--bottom">
              <span>
                Заказов: {client.ordersCount}
                {client.activeOrdersCount > 0 ? ` · активных ${client.activeOrdersCount}` : ''}
              </span>
              <span>Заявок: {client.applicationsCount}</span>
            </div>
          </Link>
        ))}
      </div>

      {total > PAGE_SIZE && (
        <div className="pager">
          <Button variant="ghost" disabled={page === 0} onClick={() => setPage(page - 1)}>
            Назад
          </Button>
          <span className="meta">
            {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} из {total}
          </span>
          <Button
            variant="ghost"
            disabled={(page + 1) * PAGE_SIZE >= total}
            onClick={() => setPage(page + 1)}
          >
            Вперёд
          </Button>
        </div>
      )}

      {creating && <NewClientModal onClose={() => setCreating(false)} />}
    </>
  );
}

/**
 * Ручное добавление клиента. Совпадение по нику — предупреждение, а не запрет:
 * тёзки и однофамильцы встречаются, и последнее слово за художником. Совпадение
 * по Telegram ID обойти нельзя — такая карточка уже есть.
 */
export function NewClientModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: (client: Client) => void;
}) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [telegramId, setTelegramId] = useState('');
  const [notes, setNotes] = useState('');
  const mutation = useMutation({
    mutationFn: (force: boolean) =>
      api<Client>('/clients', {
        method: 'POST',
        body: {
          displayName: displayName.trim(),
          username: username.trim() || null,
          telegramId: telegramId.trim() || null,
          notes: notes.trim() || null,
          force,
        },
      }),
    onSuccess: (created) => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      onCreated?.(created);
      onClose();
    },
  });
  const errors = fieldErrors(mutation.error);
  const duplicate =
    mutation.error instanceof ApiError && mutation.error.code === 'CLIENT_DUPLICATE'
      ? (mutation.error.details?.client as Client | undefined)
      : undefined;
  const existing =
    mutation.error instanceof ApiError && mutation.error.code === 'CLIENT_EXISTS'
      ? (mutation.error.details?.client as Client | undefined)
      : undefined;

  return (
    <Modal title="Новый клиент" onClose={onClose}>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate(false);
        }}
      >
        <Field label="Имя" error={errors.displayName}>
          <Input
            value={displayName}
            placeholder="Анна"
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </Field>
        <Field
          label="Ник в Telegram"
          hint="Можно просто вставить ссылку t.me/anna"
          error={errors.username}
        >
          <Input
            value={username}
            placeholder="@anna"
            onChange={(event) => setUsername(event.target.value)}
          />
        </Field>
        <Field
          label="Telegram ID"
          hint="С ником заказчик увидит заказ в своём приложении"
          error={errors.telegramId}
        >
          <Input
            inputMode="numeric"
            value={telegramId}
            onChange={(event) => setTelegramId(event.target.value)}
          />
        </Field>
        <Field label="Заметки" hint="Видно только вам" error={errors.notes}>
          <textarea
            className="input input--textarea"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>

        {existing && (
          <div className="notice notice--error stack stack--compact">
            <span>Клиент с таким Telegram ID уже есть: {existing.displayName}</span>
            <Link href={`/clients/${existing.id}`} className="button button--secondary">
              Открыть карточку
            </Link>
          </div>
        )}
        {duplicate && (
          <div className="notice notice--warning stack stack--compact">
            <span>Похожая карточка уже есть: {duplicate.displayName}</span>
            <Link href={`/clients/${duplicate.id}`} className="button button--secondary">
              Открыть её
            </Link>
            <Button
              variant="ghost"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(true)}
            >
              Всё равно добавить нового
            </Button>
          </div>
        )}
        {mutation.error && !duplicate && !existing && (
          <ErrorNotice error={mutation.error} labels={CLIENT_FIELD_LABELS} />
        )}

        <Button type="submit" wide disabled={!displayName.trim() || mutation.isPending}>
          {mutation.isPending ? 'Сохраняем…' : 'Добавить'}
        </Button>
      </form>
    </Modal>
  );
}
